import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import head from '../../components/Meta';
import UpgradeToProClubPopUp from '../../components/Upgrade/UpgradeToProClubPopUp';
import LearnMoreUtrCollegePopUp from '../../components/Upgrade/UpgradeToUtrCollege/LearnMoreUtrCollegePopUp';

import config from '../../../config';
import { showLoginPopUp } from '../../actions/login-popup-actions';
import { useStatsig } from '../../components/hooks/useStatsig';
import { isLoginPopUpNeeded, titles, userParams } from '../../components/PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { isFreeCollegeClub } from '../../utils/clubs';

import {
    userIsClubOwner,
    userIsFreeCollegeClubOwner,
    userIsProCollegeAdminOrClubOwner,
} from '../../utils/user';
import { collegeCornerSegmentTitles } from '../ClubProfiles/constants';
import styles from './CollegeCorner.scss';
import CollegeCornerCollegePlayers from './pages/CollegeCornerCollegePlayers/CollegeCornerCollegePlayers';
import CollegeCornerCollegeRankings from './pages/CollegeCornerCollegeRankings/CollegeCornerCollegeRankings';
import CollegeCornerCollegeTeam from './pages/CollegeCornerCollegeTeams/CollegeCornerCollegeTeam';
import CollegeCornerJuniorProspects from './pages/CollegeCornerJuniorProspects/CollegeCornerJuniorProspects';
import CollegeCornerOverView from './pages/CollegeCornerOverview/CollegeCornerOverview';

import useStartPowerCheckout from '../../components/hooks/useStartPowerCheckout';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../components/Analytics/constants';

const tabs = [
    {
        title: 'College Rankings',
        name: 'rankings',
        segmentEventKey: 'RANKINGS_TAB',
    },
    {
        title: 'Power 6 Rankings',
        name: 'overview',
        segmentEventKey: 'OVERVIEW_TAB',
    },
    {
        title: 'College Teams',
        name: 'teams',
        segmentEventKey: 'COLLEGE_TAB',
    },
    {
        title: 'College Players',
        name: 'players',
        segmentEventKey: 'PLAYERS_TAB',
    },
    {
        title: 'Junior Prospects',
        name: 'juniors',
        segmentEventKey: 'JUNIOR_PROSPECT_TAB',
    },
];
const publicTabs = ['overview', 'rankings'];
const initialState = {
    showLearnMoreUtrCollegePopup: false,
    showProClubPopup: false,
    clubToUpgrade: {},
};
const rankings = 'rankings';

const CollegeCorner = (props) => {
    const { match } = props;
    const { params } = match;
    const { clubId, page } = params;

    const auth = useSelector((state) => state.auth);
    const { user } = auth || {};
    const isPowerUser = user?.isPowered;

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { initializeStatsigUser, checkGate, featureGates } = useStatsig();
    const { startPowerCheckout, ctaLabel } = useStartPowerCheckout();
    const upgradeButtonLabel = ctaLabel('Upgrade to Power');
    const isRostersRolloutEnabled = checkGate(featureGates.ROSTERS_ROLLOUT);
    const [state, setState] = useState(initialState);

    /**
   * Handle click for the tabs
   * @param {*} toPage
   */

    const handleTabClick = (segmentEventKey, toPage) => {
        // segment track calls for college corner tabs click - mapped to GA4 from TabBar
        if (window?.analytics) {
            window.analytics.track(collegeCornerSegmentTitles[segmentEventKey], {
                userId: user?.id,
            });
        }
        if (!publicTabs.includes(toPage)) {
            handleShowLoginPopUp(toPage);
            return;
        }
        history.push(`/college-corner/${toPage ?? ''}`);
    };

    const handleShowLoginPopUp = (page) => {
        const actionMethod = () => history.push(`/college-corner/${page ?? ''}`);
        const userParamsNeeded = [
            userParams.CLAIMED_PROFILE,
        ];
        if (isLoginPopUpNeeded(auth, userParamsNeeded)) {
            const popupData = {
                userParamsNeeded,
                titles: titles.COLLEGE_CORNER,
                performAction: actionMethod,
                // type, // should be sent back as query while redirecting from facebook to perform proper action
            };
            dispatch(showLoginPopUp(popupData));
        } else {
            actionMethod();
        }
    };

    const userIsProCollegeClubOrganizer =
        user && userIsProCollegeAdminOrClubOwner(user);

    const userIsFreeCollegeClubOwnerValue =
        user && userIsFreeCollegeClubOwner(user);

    const getFreeCollegeClubIdOptionsUpgrade = () => {
        const clubOwnerAdminFreeCollegeClubs = user?.clubMemberships.filter(
            (clubMembership) => {
                return (
                    isFreeCollegeClub(clubMembership.tierTypeName) &&
                    userIsClubOwner(clubMembership)
                );
            },
        );

        if (clubOwnerAdminFreeCollegeClubs?.length > 0) {
            return clubOwnerAdminFreeCollegeClubs.map((clubMembership) => {
                return {
                    value: clubMembership.clubId,
                    label: clubMembership.name,
                };
            });
        }
        return null;
    };

    const handleProClubSuccess = () => {
        setTimeout(() => {
            window.location.reload();
        }, 3000);
    };

    const handleLearnMoreUtrCollegeState = (pageToAccess) => {
        if (!user) {
            handleShowLoginPopUp(pageToAccess);
        }
    };

    useEffect(() => {
        // Redirect base /college-corner to College Rankings (landing tab)
        if (page === undefined) {
            history.replace(`/college-corner/rankings${location.search || ''}`);
        }
    }, [page, history, location.search]);

    useEffect(() => {
        // For free college owner user which can not access any college corner screen except college overview
        if (!userIsProCollegeClubOrganizer && userIsFreeCollegeClubOwnerValue && !!page) {
            history.push(`/college-corner/overview?popup=true`);
        }
    }, [userIsProCollegeClubOrganizer]);

    useEffect(() => {
        const parsed = queryString.parse(location.search);
        if (Object.keys(parsed).includes('popup')) {
            handleLearnMoreUtrCollegeState('popup');
        }
        document.body.classList.add('college-corner-page');
        return () => {
            document.body.classList.remove('college-corner-page');
        };
    }, []);

    useEffect(() => {
        if (user) {
            initializeStatsigUser();
        }
    }, [user]);

    const userIsProOrFreeClubAdminOrClubOwner = () => {
        return userIsProCollegeClubOrganizer || userIsFreeCollegeClubOwnerValue;
    };

    const handleUpgradeToPowerClick = () => {
        startPowerCheckout({
            surface: PowerFunnelSurfaces.COLLEGE_CORNER,
            gatedFeature: PowerFunnelGatedFeatures.COLLEGE_CORNER_SEARCH_RESULTS,
        });
    };

    const hideMaskedP6 = userIsProOrFreeClubAdminOrClubOwner() || isPowerUser;

    return (
        <div className={`${styles.collegeCorner}`}>
            {head({
                title: 'UTR Sports | College Corner',
                description:
                    'Use UTR Sports’s advanced search filters to locate players, events, clubs, colleges and high schools',
                keywords:
                    'ITA Summer Circuit, Men’s tennis rankings, Women’s tennis rankings',
                image: `https://www.${config.siteBaseUrl}/img/utr_sports/utr-sports-logo.svg`,
                imageWidth: '930',
                imageHeight: '395',
            })}
            {state.showLearnMoreUtrCollegePopup && (
                <LearnMoreUtrCollegePopUp
                    data-testid="modal.college-corner.learn-more-college"
                    onCloseClick={() => {
                        setState((prevState) => ({
                            ...prevState,
                            showLearnMoreUtrCollegePopup: false,
                        }));
                    }}
                    handleUpgradeClick={(clubToUpgrade) => {
                        setState((prevState) => ({
                            ...prevState,
                            showLearnMoreUtrCollegePopup: false,
                            showProClubPopup: true,
                            clubToUpgrade,
                        }));
                    }}
                    clubOptionsToUpgrade={getFreeCollegeClubIdOptionsUpgrade()}
                />
            )}
            {state.showProClubPopup && state.clubToUpgrade && (
                <UpgradeToProClubPopUp
                    data-testid="modal.college-corner.upgrade-pro-club"
                    skipIntro={true}
                    onCloseClick={() => {
                        setState({
                            showProClubPopup: false,
                            clubToUpgrade: null,
                        });
                    }}
                    onSuccess={handleProClubSuccess}
                    clubId={state.clubToUpgrade.id}
                    clubTierType={{ name: state.clubToUpgrade.tierTypeName }}
                    clubName={state.clubToUpgrade.name}
                />
            )}
            <div className={`${styles.collegeCornerHeaders}`}>
                <div className={`container`} data-testid="college-corner.header.title">College corner</div>
            </div>
            <div className={`${styles.stickyTab}`}>
                <div className={`container`}>
                    <div className={`${styles.navwrap}`}>
                        <div className={`tab-bar ${styles.tabBar}`}>
                            {tabs
                                .map(({ title, name, segmentEventKey }) => (
                                    <button
                                        type="button"
                                        key={name}
                                        className={`btn btn-tab ${styles.btnTab} ${(page === name || (page === undefined && name === 'rankings')) ? styles.activeLink : ''}`}
                                        onClick={() => (
                                            handleTabClick(segmentEventKey, name)
                                        )}
                                        data-testid={`college-corner.tab-${name}`}
                                    >
                                        <div
                                            className={`d-flex flex-column align-items-start fill-height`}
                                        >
                                            <div className={`text-left`}>{title}</div>
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className={`container`}>
                <div className={`${styles.collegeCornerContent}`}>
                    {page === 'overview' && (
                        <CollegeCornerOverView
                            clubId={clubId}
                            isLoggedIn={!!user}
                            userIsProCollegeClubOwnerValue={true}
                            handleLearnMoreUtrCollegeState={handleLearnMoreUtrCollegeState}
                            hideMaskedP6={hideMaskedP6}
                            isRostersRolloutEnabled={isRostersRolloutEnabled}
                        />
                    )}
                    {page === 'teams' && (
                        <CollegeCornerCollegeTeam
                            isPowerUser={isPowerUser}
                            history={history}
                            hideMaskedP6={hideMaskedP6}
                            location={location}
                            isRostersRolloutEnabled={isRostersRolloutEnabled}
                        />
                    )}
                    {(page === rankings || page === undefined) && (
                        <CollegeCornerCollegeRankings
                            isPowerUser={isPowerUser}
                            history={history}
                            page={page}
                            hideMaskedP6={hideMaskedP6}
                            location={location}
                        />
                    )}
                    {page === 'players' && (
                        <CollegeCornerCollegePlayers
                            isPowerUser={isPowerUser}
                            history={history}
                            hideMaskedP6={hideMaskedP6}
                            location={location}
                            handleUpgradeToPowerClick={handleUpgradeToPowerClick}
                            upgradeButtonLabel={upgradeButtonLabel}
                        />
                    )}
                    {page === 'juniors' && (
                        <CollegeCornerJuniorProspects
                            isPowerUser={isPowerUser}
                            history={history}
                            hideMaskedP6={hideMaskedP6}
                            location={location}
                            handleUpgradeToPowerClick={handleUpgradeToPowerClick}
                            upgradeButtonLabel={upgradeButtonLabel}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

CollegeCorner.propTypes = {
    match: PropTypes.instanceOf(Object).isRequired,
};

export default CollegeCorner;
